import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Api } from '../api'
import {
  ApiError,
  type ApiFailure,
  type PlatformRoleCode,
  type AdminReport,
  type ReportAccessEntry,
  type RoleHolder,
} from '../api/types'
import {
  Badge,
  Button,
  Card,
  CountStat,
  EmptyState,
  Field,
  Loading,
  Notice,
  PersonCell,
  ReportPicker,
  SearchInput,
  SearchSelect,
  inputClass,
} from '../components/ui'
import { DataGrid, type Column } from '../components/DataGrid'
import { SlideOver } from '../components/SlideOver'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { FailureState } from '../components/FailureState'
import { FilterSlot, InfoStrip } from './accessWords'

/**
 * THE TWO THINGS THIS PLATFORM CAN GRANT.
 *
 * Four things decide what a person sees, and only two are ours:
 *
 *   row scope       which companies, units and teams   D365, read-only
 *   column access   AR_Mod, SLS_Mod, Sales_Cost        D365, read-only
 *   report access   may they open this at all          ours  <- here
 *   platform role   admin / permission manager /       ours  <- here
 *                   master viewer
 *
 * ===========================================================================
 * THE DESIGN PROBLEM THIS SCREEN HAS, WHICH IS NOT "ARRANGE SOME TABLES"
 *
 * Granting the report and granting the data are different acts, and only one of
 * them happens here. An administrator who does not know that grants somebody
 * the report, hears "it's empty", and concludes the platform is broken. So the
 * layout's first job is to make the seam visible at the moment of granting
 * rather than in a paragraph at the bottom of the page:
 *
 *   * the "Will see no rows" state is a column, not a footnote;
 *   * it is counted in the header strip, so it is visible before anyone scrolls;
 *   * the grant panel says it while you type, not after you submit;
 *   * the result notice names the addresses and says whose job the fix is.
 *
 * ===========================================================================
 * WHY ROLES LOOK HEAVIER THAN ACCESS
 *
 * Adding somebody to a report is routine and reversible. Making somebody a
 * Master Viewer hands them every company's balances. Rendering both as a row
 * with a dropdown would make those look like the same size of decision, so the
 * roles tab uses the privileged violet that appears nowhere else except the
 * View As banner, states each role's consequence at the point of choosing it,
 * and requires a reason for anything that reads past the security table.
 *
 * ===========================================================================
 * WHAT THIS FILE IS NOT
 *
 * It is not a control. Who may grant, the reason requirement, the last-admin
 * guard — all of it is enforced in the database and re-enforced by the API.
 * What the screen does is make the rules legible BEFORE the button is pressed,
 * so an administrator learns them from the interface instead of from a red
 * error message.
 */

interface RoleMeta {
  code: PlatformRoleCode
  name: string
  seesAllData: boolean
  /** What the holder can DO. Present tense, no jargon. */
  what: string
  /** Under the new model, only the System Master Admin gives or removes it. */
  masterOnly?: boolean
}

const ROLES: RoleMeta[] = [
  {
    code: 'SystemAdmin',
    name: 'System Admin',
    seesAllData: true,
    what: 'Runs the platform: appoints roles, opens maintenance windows, and reads every company.',
  },
  {
    code: 'MasterViewer',
    name: 'Master Viewer',
    seesAllData: true,
    what: 'Reads every company’s figures regardless of the permission table. Grants nothing.',
  },
]

/**
 * THE NEW MODEL'S SYSTEM ROLES (048; 05 section 4.12; D7, D24). One per person.
 * The System Master Admin is not among them: one person, given and removed only
 * by db/deploy/set-system-master-admin.sql, never on this screen. The database
 * enforces every rule (017, under v2); the cards only say them first.
 */
const ROLES_V2: RoleMeta[] = [
  {
    code: 'SystemAdmin',
    name: 'System Admin',
    seesAllData: true,
    masterOnly: true,
    what: 'Every report; the Access screens and approving entry; gives System Development and System Master Reader; developer mode; Login As.',
  },
  {
    code: 'SystemDevelopment',
    name: 'System Development',
    seesAllData: true,
    what: 'Every report; developer mode; Login As. Changes nobody’s access.',
  },
  {
    code: 'MasterViewer',
    name: 'System Master Reader',
    seesAllData: true,
    what: 'Every report, and nothing else. Cannot use Login As.',
  },
]

/**
 * A short, human error.
 *
 * ApiError.message is the decoded failure. For the database's own guards this
 * is its exact wording, passed through on purpose: "That is the last System
 * Admin. Appoint another before removing this one." tells an administrator what
 * to do next. "Bad Request" does not.
 */
function messageOf(e: unknown): string {
  return e instanceof ApiError ? e.message : 'That did not work. Nothing was changed.'
}

function when(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Split on commas and new lines. Matches what the database's STRING_SPLIT does. */
function parseEmails(raw: string): string[] {
  return [...new Set(raw.split(/[\n,;]/).map((e) => e.trim()).filter(Boolean))]
}

/* =========================================================================
   REPORT ACCESS
   ========================================================================= */

export function ReportAccessTab({
  api,
  reports,
  reportSlug,
  onPickReport,
}: {
  api: Api
  /** The registry, fetched once by the page. Null while it is still loading. */
  reports: AdminReport[] | null
  /** WHICH report is being administered. No default — see ReportPicker. */
  reportSlug: string
  onPickReport: (slug: string) => void
}) {
  const [rows, setRows] = useState<ReportAccessEntry[] | null>(null)
  const [search, setSearch] = useState('')
  const [granting, setGranting] = useState(false)
  const [confirm, setConfirm] = useState<ReportAccessEntry | null>(null)
  const [busy, setBusy] = useState(false)
  // For the grant and revoke actions only. A failed READ of the list is kept
  // apart in `failure`: it used to land here and set the list to [], so under a
  // dismissible red notice the grid said "Nobody has been granted Sales" with a
  // count of 0 — and an administrator could believe it and start re-granting.
  const [error, setError] = useState<string | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  // Kept until dismissed. An administrator who pasted eight addresses needs to
  // know which the ERP has never heard of, and a toast that fades in three
  // seconds is not where that belongs.
  const [result, setResult] = useState<{ added: number; alreadyHad: number; unknown: string[] } | null>(null)

  const report = reports?.find((r) => r.slug === reportSlug)
  const reportName = report?.displayName ?? reportSlug

  const load = useCallback(() => {
    if (!reportSlug) return
    setRows(null)
    setResult(null)
    setError(null)
    setFailure(null)
    // rows stays null on failure, so the count reads '—' rather than 0.
    api
      .reportAccess(reportSlug)
      .then(setRows)
      .catch((e: unknown) =>
        setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' }),
      )
  }, [api, reportSlug])

  // Re-reads when the chosen report changes, and clears the previous result and
  // error: a "3 people granted" notice left over from another report is worse
  // than no notice at all.
  useEffect(load, [load])

  const visible = useMemo(() => {
    if (!rows) return []
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => (r.email + ' ' + (r.displayName ?? '')).toLowerCase().includes(q))
  }, [rows, search])

  const unknownCount = rows?.filter((r) => !r.knownToD365).length ?? 0

  const revoke = async () => {
    if (!confirm) return
    setBusy(true); setError(null); setResult(null)
    try {
      await api.revokeReportAccess(confirm.email, reportSlug)
      setConfirm(null)
      load()
    } catch (e) {
      setError(messageOf(e))
      setConfirm(null)
    } finally {
      setBusy(false)
    }
  }

  const columns: Column<ReportAccessEntry>[] = [
    {
      key: 'person',
      header: 'User',
      align: 'left',
      // A rendered cell cannot be compared, so the sort is told to use the
      // name the reader sees rather than the node it is wrapped in.
      sortValue: (r) => r.displayName ?? r.email,
      render: (r) => <PersonCell name={r.displayName} email={r.email} />,
    },
    {
      key: 'knownToD365',
      header: 'Data scope',
      align: 'left',
      // THE COLUMN THIS TAB EXISTS FOR.
      //
      // Access and data scope are separate gates and only the first is ours, so
      // a person can hold the report and still open it to an empty grid.
      // Without this the administrator grants somebody, hears "it doesn't
      // work", and has nothing to look at. It is a WARNING, not an error: the
      // grant was correct and the ERP has simply not caught up.
      render: (r) =>
        r.knownToD365 ? (
          <span className="text-(--color-ink-3)">From D365</span>
        ) : (
          <Badge tone="warn">Will see no rows</Badge>
        ),
    },
    { key: 'grantedBy', header: 'Granted by', align: 'left', render: (r) => r.grantedBy || '—' },
    { key: 'grantedUtc', header: 'When', align: 'left', render: (r) => when(r.grantedUtc) },
    {
      key: 'reason',
      header: 'Reason',
      align: 'left',
      // Clamped: a free-text sentence would size the column to the longest one
      // anybody has typed and push the row's action off the right edge.
      clamp: 34,
      render: (r) =>
        r.reason ? (
          <span className="text-(--color-ink-2)">{r.reason}</span>
        ) : (
          <span className="text-(--color-ink-4)">Not recorded</span>
        ),
    },
    {
      key: 'remove',
      header: ' ',
      align: 'right',
      stickyRight: true,
      render: (r) => (
        <button
          type="button"
          disabled={busy}
          onClick={(e) => { e.stopPropagation(); setConfirm(r) }}
          className="rounded px-2 py-1 text-(length:--text-small) font-medium text-(--color-ink-3) transition-colors hover:bg-(--color-overdue)/8 hover:text-(--color-overdue) disabled:opacity-40"
        >
          Remove
        </button>
      ),
    },
  ]

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {(error || result) && (
        <div className="shrink-0 space-y-2">
          {error && <Notice tone="bad" onDismiss={() => setError(null)}>{error}</Notice>}
          {result && (
            <Notice
              tone={result.unknown.length ? 'warn' : 'good'}
              title={
                result.added === 0 && result.alreadyHad > 0
                  ? 'Everyone on that list already had access.'
                  : `${result.added} ${result.added === 1 ? 'user' : 'users'} granted access.` +
                    (result.alreadyHad ? ` ${result.alreadyHad} already had it.` : '')
              }
              onDismiss={() => setResult(null)}
            >
              {result.unknown.length > 0 ? (
                <>
                  {result.unknown.length === 1 ? 'This address is' : 'These addresses are'} not in
                  D365&rsquo;s permission data, so they will open the report to an empty grid:{' '}
                  <span className="font-mono text-(--color-ink)">{result.unknown.join(', ')}</span>.
                  The access grant is correct and is already in place &mdash; what is missing is
                  their scope in D365, which the ERP team owns.
                </>
              ) : (
                'Everyone granted also has scope in D365, so they will see their rows straight away.'
              )}
            </Notice>
          )}
        </div>
      )}

      <Card className="flex min-h-0 flex-1 flex-col">
        {/* THE HEADER STRIP: state on the left, actions on the right.
            Counts before controls, because the first question on arriving at
            this screen is "what is true right now", not "what can I press". */}
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 border-b border-(--color-line) px-5 py-3">
          {/* items-stretch so a two-line label does not leave its neighbour
              short. The hairline divider went with the cards' own borders. */}
          <div className="flex flex-wrap items-stretch gap-2">
            <CountStat label={`can open ${reportName}`} value={rows?.length ?? '—'} />
            {unknownCount > 0 && (
              <CountStat
                label="will see no rows"
                value={unknownCount}
                tone="warn"
                title="Granted the report, but absent from D365's permission data. Their scope is the ERP team's to add."
              />
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <ReportPicker reports={reports} value={reportSlug} onChange={onPickReport} busy={busy} />
            <div className="w-56">
              <SearchInput
                label="Search the access list"
                placeholder="Search name or email…"
                value={search}
                onChange={setSearch}
              />
            </div>
            {/* Off while the list is unreadable: the panel's "already on the
                list" check would be comparing against nothing. */}
            <Button variant="primary" onClick={() => setGranting(true)} disabled={!reportSlug || !!failure}>
              Grant access
            </Button>
          </div>
        </div>

        {failure ? (
          <FailureState failure={failure} onRetry={load} />
        ) : rows === null ? (
          <Loading label="Reading the access list…" />
        ) : rows.length === 0 ? (
          <EmptyState
            title={`Nobody has been granted ${reportName}.`}
            detail="Until somebody is on this list, the report opens to a refusal for everyone — including users with full scope in D365. Use Grant access to add the first user."
          />
        ) : visible.length === 0 ? (
          <EmptyState
            title={`Nobody on the access list matches “${search}”.`}
            detail="This searches the users who can open the report, not everyone in D365."
          />
        ) : (
          <DataGrid fill columns={columns} rows={visible} />
        )}
      </Card>

      {granting && (
        <GrantAccessPanel
          api={api}
          reportSlug={reportSlug}
          reportName={reportName}
          existing={rows ?? []}
          onClose={() => setGranting(false)}
          onDone={(r) => { setGranting(false); setResult(r); setError(null); load() }}
          onError={(m) => { setError(m) }}
        />
      )}

      {confirm && (
        <ConfirmDialog
          title={`Remove access to ${reportName}?`}
          detail={
            <>
              <strong className="font-semibold text-(--color-ink)">
                {confirm.displayName || confirm.email}
              </strong>{' '}
              will no longer be able to open {reportName}. Their D365 scope is untouched,
              so this can be undone by granting the report again.
            </>
          }
          confirmLabel="Remove access"
          busy={busy}
          onConfirm={() => void revoke()}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  )
}

/** The grant form, in a panel. See SlideOver for why it is not inline. */
function GrantAccessPanel({
  api,
  reportSlug,
  reportName,
  existing,
  onClose,
  onDone,
  onError,
}: {
  api: Api
  /** The report being granted. Sent explicitly; the API refuses to guess. */
  reportSlug: string
  reportName: string
  existing: ReportAccessEntry[]
  onClose: () => void
  onDone: (r: { added: number; alreadyHad: number; unknown: string[] }) => void
  onError: (message: string) => void
}) {
  const [emails, setEmails] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const parsed = parseEmails(emails)
  // Told BEFORE submitting, not after. Re-granting is a harmless no-op, but an
  // administrator who does not know somebody already has access is probably
  // looking at the wrong name.
  const duplicates = parsed.filter((e) => existing.some((x) => x.email.toLowerCase() === e.toLowerCase()))
  const malformed = parsed.filter((e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e))

  const submit = async () => {
    if (!parsed.length || busy) return
    setBusy(true)
    try {
      const r = await api.grantReportAccess({
        emails: parsed.join(','),
        reportSlug,
        reason: reason.trim() || undefined,
      })
      onDone({ added: r.added, alreadyHad: r.alreadyHad, unknown: r.unknownEmails })
    } catch (e) {
      onError(messageOf(e))
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <SlideOver
      title={`Grant access to ${reportName}`}
      description="This decides who may OPEN the report. What they then see inside it comes from D365 and is not set here."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} disabled={busy || !parsed.length}>
            {busy ? 'Granting…' : parsed.length > 1 ? `Grant to ${parsed.length} users` : 'Grant access'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label="Email addresses"
          hint="One per line, or separated by commas. Paste a list — re-granting somebody who already has access does nothing."
        >
          <textarea
            className={`${inputClass} min-h-28 resize-y font-mono text-(length:--text-body)`}
            placeholder={'nour.habli@redachem.com\natif.majeed@redachem.com'}
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
          />
        </Field>

        {parsed.length > 0 && (
          <div className="space-y-2">
            {malformed.length > 0 && (
              <Notice tone="bad">
                {malformed.length === 1 ? 'This does not look like an address' : 'These do not look like addresses'}:{' '}
                <span className="font-mono text-(--color-ink)">{malformed.join(', ')}</span>. They can
                still be granted, but nobody will ever sign in as them.
              </Notice>
            )}
            {duplicates.length > 0 && (
              <Notice tone="info">
                Already on the list:{' '}
                <span className="font-mono text-(--color-ink)">{duplicates.join(', ')}</span>. Granting
                again changes nothing.
              </Notice>
            )}
          </div>
        )}

        <Field
          label="Reason Of The Change"
          hint="Optional, and recorded against your name. The next user to review this list reads it to decide whether the access is still warranted."
        >
          <input
            className={inputClass}
            placeholder="e.g. Joined the AR team"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>

        {/* THE SEAM, STATED WHILE THEY TYPE.
            This is the single most common misunderstanding on the screen, and
            the cheapest place to prevent it is here — before the grant, not in
            a support call afterwards. */}
        <div className="rounded-md border border-(--color-line) bg-(--color-surface-2) px-4 py-3 text-(length:--text-small) leading-relaxed text-(--color-ink-2)">
          <div className="font-semibold text-(--color-ink)">Access is not data.</div>
          <p className="mt-1">
            Which companies, units and teams a user can see comes from D365, and this platform
            reads it rather than setting it. Somebody granted the report who has no scope there will
            open it to an empty grid &mdash; the list marks them{' '}
            <Badge tone="warn">Will see no rows</Badge> so you find out now rather than later.
          </p>
        </div>
      </div>
    </SlideOver>
  )
}

/* =========================================================================
   PLATFORM ROLES — System Admin only
   ========================================================================= */

export function PlatformRolesTab({ api, actor, account = null, isV2 = false }: {
  api: Api
  /** The person at the keyboard. */
  actor: string | null
  /** The account this session is (the person acted as under Login As): the database judges it (017). */
  account?: string | null
  /** The new model: four system roles, one per person, and their rules (048; 05 section 4.12). */
  isV2?: boolean
}) {
  const [rows, setRows] = useState<RoleHolder[] | null>(null)
  const [search, setSearch] = useState('')
  const [appointing, setAppointing] = useState(false)
  const [confirm, setConfirm] = useState<RoleHolder | null>(null)
  // The new model records why a system role was removed (51178).
  const [revokeReason, setRevokeReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  // A failed read, kept apart from an empty list. Setting the list to [] on
  // failure put "0 administer the platform" and the lockout message ("do not
  // sign out") on screen for what was only a 500 — a fact never read, and an
  // instruction that only makes sense in a real lockout.
  const [failure, setFailure] = useState<ApiFailure | null>(null)

  const load = () => {
    setRows(null)
    setFailure(null)
    api
      .roleHolders()
      .then(setRows)
      .catch((e: unknown) =>
        setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' }),
      )
  }
  useEffect(load, [api])

  // One role shown, picked in the Role dropdown (owner, 28 Oct 2026); '' is every role.
  const [roleFilter, setRoleFilter] = useState('')
  // The System Master Admin is a card under either model whenever somebody holds it:
  // the rows count it as an administrator under both, so the cards must add up.
  const masterCard = { code: 'SystemMasterAdmin', name: 'System Master Admin', what: 'Everything an admin does, and the only one who gives or removes System Admin. Set by a database script.' }
  const roleCards: { code: string; name: string; what: string }[] = isV2
    ? [masterCard, ...ROLES_V2]
    : rows?.some((r) => r.roleCode === 'SystemMasterAdmin') ? [masterCard, ...ROLES] : ROLES

  const holding = (code: string) => (rows ? rows.filter((x) => x.roleCode === code).length : null)
  const visible = useMemo(() => {
    if (!rows) return []
    const q = search.trim().toLowerCase()
    return rows.filter((r) =>
      (!roleFilter || r.roleCode === roleFilter)
      && (!q || (r.email + ' ' + (r.displayName ?? '') + ' ' + r.roleName).toLowerCase().includes(q)),
    )
  }, [rows, search, roleFilter])

  // The System Master Admin administers too, and 017's last-administrator rule
  // counts both, under either model.
  const admins = rows?.filter((r) => r.roleCode === 'SystemAdmin' || r.roleCode === 'SystemMasterAdmin') ?? []
  const allData = rows?.filter((r) => r.seesAllData).length ?? 0
  // Only the System Master Admin gives or removes System Admin (v2). Read from
  // the list, the one place this screen learns which system role its user holds.
  // The ACCOUNT's role: under Login As the database judges the account acted as.
  const isMaster = isV2 && !!rows?.some((r) => r.roleCode === 'SystemMasterAdmin' && same(r.email, account ?? actor))

  const closeConfirm = () => {
    setConfirm(null)
    setRevokeReason('')
  }

  const revoke = async () => {
    if (!confirm) return
    setBusy(true); setError(null); setDone(null)
    try {
      await api.setPlatformRole({ email: confirm.email, roleCode: confirm.roleCode, grant: false, reason: revokeReason.trim() || undefined })
      setDone(`${confirm.displayName || confirm.email} no longer holds ${confirm.roleName}.`)
      closeConfirm()
      load()
    } catch (e) {
      // The API passes the database's wording through, so this reads "That is
      // the last System Admin. Appoint another before removing this one."
      setError(messageOf(e))
      closeConfirm()
    } finally {
      setBusy(false)
    }
  }

  const columns: Column<RoleHolder>[] = [
    {
      key: 'person',
      header: 'User',
      align: 'left',
      stickyLeft: true,
      sortValue: (r) => r.displayName ?? r.email,
      render: (r) => (
        <PersonCell
          name={r.displayName}
          email={r.email}
          note={
            r.email === actor ? (
              <span className="shrink-0 rounded bg-(--color-surface-3) px-1.5 py-px text-(length:--text-micro) font-semibold uppercase tracking-[0.04em] text-(--color-ink-3)">
                You
              </span>
            ) : undefined
          }
        />
      ),
    },
    {
      key: 'roleName',
      header: 'Role',
      align: 'left',
      // seesAllData is the one fact about a role an administrator will not
      // remember and the one that matters most. Violet, which appears nowhere
      // else except the View As banner — so its only possible meaning is "this
      // reads past the security table".
      render: (r) => (
        <span className="flex items-center gap-2">
          <span className="font-medium text-(--color-ink)">{r.roleName}</span>
          {r.seesAllData && <Badge tone="privileged">Full data access</Badge>}
        </span>
      ),
    },
    { key: 'grantedBy', header: 'Appointed by', align: 'left', render: (r) => r.grantedBy || '—' },
    { key: 'grantedUtc', header: 'When', align: 'left', render: (r) => when(r.grantedUtc) },
    {
      key: 'reason',
      header: 'Reason',
      align: 'left',
      // Clamped: a free-text sentence would size the column to the longest one
      // anybody has typed and push the row's action off the right edge.
      clamp: 34,
      render: (r) =>
        r.reason ? (
          <span className="text-(--color-ink-2)">{r.reason}</span>
        ) : (
          <span className="text-(--color-ink-4)">Not recorded</span>
        ),
    },
    {
      key: 'revoke',
      header: ' ',
      align: 'right',
      stickyRight: true,
      render: (r) => {
        // DISABLED WITH A REASON, NOT HIDDEN.
        //
        // Both guards live in the database and will refuse regardless. Showing
        // the button greyed with the rule in its tooltip teaches why; removing
        // it entirely reads as a rendering bug, and the administrator goes
        // looking for the control instead of learning the rule.
        const adminRow = r.roleCode === 'SystemAdmin' || r.roleCode === 'SystemMasterAdmin'
        const lastAdmin = adminRow && admins.length <= 1
        // Today only an admin's own admin role is guarded; under v2 every own role
        // is, the account's and the person's at the keyboard alike (51174).
        const self = isV2 ? same(r.email, actor) || same(r.email, account) : r.roleCode === 'SystemAdmin' && r.email === actor
        // Never on this screen, under either model: 017 refuses it before its v2 rules.
        const scriptOnly = r.roleCode === 'SystemMasterAdmin'
        const masterOnly = isV2 && r.roleCode === 'SystemAdmin' && !isMaster
        const blocked = lastAdmin || self || scriptOnly || masterOnly
        const scriptWhy = 'The System Master Admin is set and removed only by a reviewed database script.'
        const lastWhy = 'This is the last System Admin. Appoint another before removing this one.'
        // In 017's order: today the last System Admin first, then your own; under v2
        // the Master Admin's rule, then your own, then the last administrator.
        const why = scriptOnly
          ? scriptWhy
          : !isV2
            ? lastAdmin ? lastWhy : self ? 'Ask another System Admin to remove your own admin role.' : undefined
            : masterOnly
              ? 'Only the System Master Admin can remove a System Admin.'
              : self
                ? 'You cannot change your own system role. Ask another administrator.'
                : lastAdmin ? lastWhy : undefined

        return (
          <button
            type="button"
            disabled={busy || blocked}
            title={why}
            onClick={(e) => { e.stopPropagation(); setConfirm(r) }}
            className="rounded px-2 py-1 text-(length:--text-small) font-medium text-(--color-ink-3) transition-colors hover:bg-(--color-overdue)/8 hover:text-(--color-overdue) disabled:cursor-not-allowed disabled:text-(--color-ink-4) disabled:hover:bg-transparent disabled:hover:text-(--color-ink-4)"
          >
            Revoke
          </button>
        )
      },
    },
  ]

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {(error || done) && (
        <div className="shrink-0 space-y-2">
          {error && <Notice tone="bad" onDismiss={() => setError(null)}>{error}</Notice>}
          {done && <Notice tone="good" onDismiss={() => setDone(null)}>{done}</Notice>}
        </div>
      )}

      <Card className="flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 flex-col gap-3 border-b border-(--color-line) px-5 py-(--band-y)">
          {/* One card a role: how many hold it and what it gives; the Role dropdown does
              the filtering (owner, 28 Oct 2026). '—' until the list has been read: a 0 here
              would be a claim. */}
          <div className={`grid grid-cols-1 gap-2 @3xl:grid-cols-2 ${isV2 ? '@6xl:grid-cols-4' : ''}`}>
            {roleCards.map((r) => (
              <div key={r.code} className="flex flex-col items-start gap-1 rounded-md border border-(--color-line) bg-(--color-surface) px-3 py-2.5">
                <span className="flex w-full items-center gap-2">
                  <span className="font-semibold text-(--color-ink)">{r.name}</span>
                  <span className="ml-auto text-(length:--text-lg) font-semibold text-(--color-ink) tabular-nums">{holding(r.code) ?? '—'}</span>
                </span>
                {/* Whole, never clamped: it is the role's rule, and this is the one place it is written. */}
                <span className="text-(length:--text-small) leading-snug text-(--color-ink-3)">{r.what}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <FilterSlot label="Role" wide>
              <SearchSelect label="Role" value={roleFilter} onChange={setRoleFilter}
                options={[{ value: '', label: 'All roles' },
                  ...roleCards.map((r) => ({ value: r.code, label: holding(r.code) === null ? r.name : `${r.name} · ${holding(r.code)}` }))]} />
            </FilterSlot>
            <div className="flex min-w-[240px] flex-1 flex-col gap-1 @6xl:max-w-[360px]">
              <span className="eyebrow">Find a holder</span>
              <SearchInput label="Search role holders" placeholder="Name, email or role…" value={search} onChange={setSearch} />
            </div>
            <span className="flex flex-wrap items-center gap-2 pb-1.5 text-(length:--text-small) text-(--color-ink-3)">
              {rows && <>{admins.length} {admins.length === 1 ? 'administers' : 'administer'} the platform</>}
              {rows && (
                <span title={isV2 ? 'Every system role reads every report, past the HR settings.' : 'System Admin and Master Viewer bypass the permission table entirely.'}>
                  <Badge tone={allData > 0 ? 'privileged' : 'neutral'}>{allData} with full data access</Badge>
                </span>
              )}
            </span>
            <span className="ml-auto">
              <Button variant="primary" onClick={() => setAppointing(true)}>
                Appoint a role
              </Button>
            </span>
          </div>
        </div>

        {rows && visible.length > 0 && (
          <div className="flex shrink-0 items-center gap-3 border-b border-(--color-line) bg-(--color-surface-2) px-5 py-1.5 text-(length:--text-small) text-(--color-ink-3)">
            Showing {visible.length} of {rows.length} {rows.length === 1 ? 'holder' : 'holders'}
            {roleFilter ? ` · ${roleCards.find((r) => r.code === roleFilter)?.name ?? roleFilter} only` : ''}.
            {roleFilter && (
              <button type="button" onClick={() => setRoleFilter('')} className="font-medium text-(--color-navy-700) hover:underline">Show every role</button>
            )}
          </div>
        )}

        {failure ? (
          <FailureState failure={failure} onRetry={load} />
        ) : rows === null ? (
          <Loading label="Reading platform roles…" />
        ) : rows.length === 0 ? (
          <EmptyState
            title={isV2 ? 'Nobody holds a system role.' : 'Nobody holds a platform role.'}
            detail="With no System Admin, nothing on this screen can be changed from the platform itself. If you are seeing this, your own role has not been read back correctly — do not sign out."
          />
        ) : visible.length === 0 ? (
          <EmptyState
            title={search
              ? `No ${roleFilter ? `${roleCards.find((r) => r.code === roleFilter)?.name ?? roleFilter} ` : 'role '}holder matches “${search}”.`
              : 'Nobody holds this role.'}
            detail={roleFilter ? 'Choose All roles in the Role dropdown to show every role.' : undefined}
          />
        ) : (
          <DataGrid fill columns={columns} rows={visible} />
        )}

        <InfoStrip>
          {isV2 ? (
            <>
              Every appointment and removal here is recorded against your name, with the role, the
              user and the reason. A user holds one system role. The System Master Admin is set
              only by a reviewed database script, and only they give or remove System Admin. Nobody
              changes their own role, and the last administrator cannot be removed.
            </>
          ) : (
            <>
              Every appointment and revocation here is recorded against your name, with the role, the
              user and the reason. The platform will not let the last System Admin be removed, or let
              you remove your own — not because either is a security risk, but because a platform nobody
              can re-enter is worse than one that is briefly wrong.
            </>
          )}
        </InfoStrip>
      </Card>

      {appointing && (
        <AppointRolePanel
          api={api}
          existing={rows ?? []}
          isV2={isV2}
          isMaster={isMaster}
          selves={[actor, account]}
          onClose={() => setAppointing(false)}
          onDone={(m) => { setAppointing(false); setDone(m); setError(null); load() }}
          onError={(m) => setError(m)}
        />
      )}

      {confirm && (
        <ConfirmDialog
          title={`Revoke ${confirm.roleName}?`}
          detail={
            <>
              <strong className="font-semibold text-(--color-ink)">
                {confirm.displayName || confirm.email}
              </strong>{' '}
              will lose {confirm.roleName}
              {confirm.seesAllData && (
                <>
                  {' '}
                  {isV2 ? 'and will no longer see every report' : 'and will no longer read companies outside their own D365 scope'}
                </>
              )}
              . {isV2 ? 'They keep what their HR Role and settings give them.' : 'Their report access and D365 scope are untouched.'}
              {isV2 && (
                <input
                  className={`${inputClass} mt-3`}
                  placeholder="Reason Of The Change (required, kept with the record)"
                  aria-label="Reason Of The Change: removing the role (required)"
                  aria-required="true"
                  maxLength={256}
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                />
              )}
            </>
          }
          confirmLabel="Revoke role"
          busy={busy}
          confirmDisabled={isV2 && !revokeReason.trim()}
          focusInput={isV2}
          onConfirm={() => void revoke()}
          onCancel={closeConfirm}
        />
      )}
    </div>
  )
}

/**
 * Appointing a role.
 *
 * The role is chosen from CARDS, not a dropdown, and that is the most important
 * decision in this file. A <select> shows one line — "Master Viewer" — and hides
 * the only thing that matters about it, which is that the holder reads every
 * company's balances. The consequence has to be on screen at the moment of
 * choosing, not one interaction away.
 */
/** One address, however it is cased. */
function same(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.toLowerCase() === b.toLowerCase()
}

function AppointRolePanel({
  api,
  existing,
  isV2,
  isMaster,
  selves,
  onClose,
  onDone,
  onError,
}: {
  api: Api
  existing: RoleHolder[]
  isV2: boolean
  /** The new model's System Master Admin: the only one who gives System Admin. */
  isMaster: boolean
  /** The person at the keyboard and the account: neither changes their own system role (51174). */
  selves: (string | null)[]
  onClose: () => void
  onDone: (message: string) => void
  onError: (message: string) => void
}) {
  const [email, setEmail] = useState('')
  // MasterViewer is the default because it is the SAFEST role offered: it
  // reads everything and changes nothing, where System Admin can appoint
  // roles. Permission Manager used to hold this slot precisely because it was
  // the least powerful, and its removal left the default pointing at the most.
  const [roleCode, setRoleCode] = useState<PlatformRoleCode>('MasterViewer')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const roles = isV2 ? ROLES_V2 : ROLES
  const chosen = roles.find((r) => r.code === roleCode)!
  const target = email.trim()
  const alreadyHas = existing.some(
    (r) => r.email.toLowerCase() === target.toLowerCase() && r.roleCode === roleCode,
  )
  // The new model, said before the press: nobody changes their own role (51174),
  // and a person holds one system role (51177).
  const isSelf = isV2 && selves.some((s) => same(s, target))
  const holdsOther = isV2 && !isSelf
    ? existing.find((r) => r.email.toLowerCase() === target.toLowerCase() && r.roleCode !== roleCode)
    : undefined
  const locked = (r: RoleMeta) => isV2 && !!r.masterOnly && !isMaster

  // Mirrors the database's own rule (51173) so the requirement is visible
  // before the press rather than discovered by a rejection. The database still
  // enforces it — this only decides when the button lights up.
  const reasonRequired = chosen.seesAllData
  const canSubmit = !!target && !busy && !alreadyHas && !isSelf && !holdsOther && !locked(chosen) && (!reasonRequired || !!reason.trim())

  const submit = async () => {
    if (!canSubmit) return
    setBusy(true)
    try {
      await api.setPlatformRole({
        email: target,
        roleCode,
        grant: true,
        reason: reason.trim() || undefined,
      })
      onDone(`${target} now holds ${chosen.name}.`)
    } catch (e) {
      onError(messageOf(e))
      onClose()
    } finally {
      setBusy(false)
    }
  }

  return (
    <SlideOver
      title={isV2 ? 'Appoint a system role' : 'Appoint a platform role'}
      description={isV2
        ? 'System roles exist only in MBI. They are separate from the HR Role and its settings.'
        : 'Platform roles exist only in MBI. They are separate from D365 scope and from report access.'}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} disabled={!canSubmit}>
            {busy ? 'Appointing…' : 'Appoint'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label="User"
          hint="Must already have signed in at least once, so the platform knows who they are."
        >
          <input
            className={inputClass}
            placeholder="name@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>

        {alreadyHas && (
          <Notice tone="info">
            <span className="font-mono text-(--color-ink)">{target}</span> already holds{' '}
            {chosen.name}.
          </Notice>
        )}
        {isSelf && (
          <Notice tone="info">You cannot change your own system role. Ask another administrator.</Notice>
        )}
        {holdsOther && (
          <Notice tone="info">
            <span className="font-mono text-(--color-ink)">{target}</span> already holds{' '}
            {holdsOther.roleName}. A user holds one system role: remove that one first.
          </Notice>
        )}

        <fieldset>
          <legend className="mb-1.5 block text-(length:--text-small) font-medium text-(--color-ink-2)">
            Role
          </legend>
          <div className="space-y-2">
            {roles.map((r) => {
              const on = r.code === roleCode
              const off = locked(r)
              return (
                <label
                  key={r.code}
                  // items-start, or the radio stretches to the card's height and
                  // its glyph renders at the vertical centre — level with the
                  // description instead of the role it selects.
                  className={`flex items-start gap-3 rounded-md border px-3.5 py-3 transition-colors ${off ? 'cursor-not-allowed' : 'cursor-pointer'} ${
                    off
                      ? 'border-(--color-line) opacity-60'
                      : on
                      ? r.seesAllData
                        ? 'border-(--color-privileged) bg-(--color-privileged)/6'
                        : 'border-(--color-navy-700) bg-(--color-navy-50)'
                      : 'border-(--color-line) hover:border-(--color-line-strong) hover:bg-(--color-surface-2)'
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    className="mt-0.5 accent-(--color-navy-700)"
                    checked={on}
                    disabled={off}
                    onChange={() => setRoleCode(r.code)}
                  />
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-(--color-ink)">{r.name}</span>
                      {r.seesAllData && <Badge tone="privileged">Full data access</Badge>}
                    </span>
                    <span className="mt-1 block text-(length:--text-small) leading-relaxed text-(--color-ink-3)">
                      {r.what}
                    </span>
                    {isV2 && r.masterOnly && (
                      <span className="mt-1 block text-(length:--text-small) font-medium text-(--color-ink-2)">
                        Only the System Master Admin can give it.
                      </span>
                    )}
                  </span>
                </label>
              )
            })}
          </div>
          {isV2 && (
            <p className="mt-2 text-(length:--text-small) leading-relaxed text-(--color-ink-3)">
              System Master Admin is not given here: one user, set only by a reviewed database script.
            </p>
          )}
        </fieldset>

        <Field
          label={reasonRequired ? 'Reason Of The Change (required)' : 'Reason Of The Change'}
          hint={
            reasonRequired
              ? `Required for any role that reads past ${isV2 ? 'the HR settings' : 'the permission table'}. “Why does this user see the whole book?” is the first question an auditor asks, and the answer has to have been written down at the time.`
              : 'Optional, and recorded against your name.'
          }
        >
          <input
            className={inputClass}
            placeholder={reasonRequired ? 'e.g. Group finance controller — board reporting' : 'e.g. Runs the quarterly access review'}
            maxLength={256}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>

        {chosen.seesAllData && (
          <Notice tone="warn" title="This role bypasses data security.">
            {/* Business Unit and Business Team under the new model (owner, 30 Oct 2026); the old model's words stay. */}
            {chosen.name} reads every company, {isV2 ? 'Business Unit and Business Team' : 'unit and team'} regardless of {isV2 ? 'their HR Role and settings' : 'what D365 grants them'}.
            Their reads are flagged as privileged in the audit log.
          </Notice>
        )}
      </div>
    </SlideOver>
  )
}
